import { Injectable, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';

// Extend Navigator interface to include Web Serial API
declare global {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface Navigator {
        serial: Serial;
    }
}

export type GetPacketRetVal = {
    size?: number;
    spill?: Uint8Array;
    endReason?: 'EOT' | 'DONE';
};

export type SerialDeviceInfo = {
    portName: string;
    manufacturer?: string;
    serialNumber?: string;
    vendorId?: string;
    productId?: string;
};

export type SerialConnectionConfig = {
    baudRate: number;
    dataBits: 7 | 8;
    stopBits: 1 | 2;
    parity: 'none' | 'even' | 'odd';
    flowControl: boolean;
};
export const SZ_CODES = {
    SOH: 0x01,
    STX: 0x02,
    EOT: 0x04,
    ACK: 0x06,
    NAK: 0x15,
    C: 0x43
};

// The packet payload sizes that the header bytes represent, and the overhead that the protocol requires (header byte, packet numbers anc CRC)
export const szPacketSize = {
    [SZ_CODES.SOH]: 128,
    [SZ_CODES.STX]: 1024,
    overhead: 5
};

// Todo: Maybe move more errors here. INVALID_HEADER_BYTE was added first because it is used in an exact string match.
export const IMPORT_ERRORS = {
    invalidHeaderByte: 'Invalid packet header byte.'
};

@Injectable({
    providedIn: 'root'
})
export class SerialService implements OnDestroy {
    private port: SerialPort | null = null;
    private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
    private connectionSession = 0;

    public get connectionSessionId(): number {
        return this.connectionSession;
    }

    private connectionStatus = new BehaviorSubject(false);
    private errorSubject = new Subject<Error>();

    private readonly ackByte = new Uint8Array([SZ_CODES.ACK]);

    public connectionStatus$ = this.connectionStatus.asObservable();
    public error$ = this.errorSubject.asObservable();

    // Buffer for incoming serial data
    private dataBuffer: number[] = [];
    private isReadLoopActive = false;
    private dataReceivedListeners: (() => void)[] = [];

    /**
     * Check if Web Serial API is supported
     */
    public isSupported(): boolean {
        return 'serial' in navigator;
    }

    /**
     * Get available serial ports
     */
    public async getAvailablePorts(): Promise<SerialDeviceInfo[]> {
        if (!this.isSupported()) {
            throw new Error('Web Serial API is not supported in this browser');
        }

        try {
            const ports = await navigator.serial.getPorts();
            return ports.map((port: SerialPort) => ({
                portName: port.getInfo().usbVendorId ? `USB Device` : 'Serial Port',
                manufacturer: port.getInfo().usbVendorId?.toString(),
                vendorId: port.getInfo().usbVendorId?.toString(16),
                productId: port.getInfo().usbProductId?.toString(16)
            }));
        } catch (error) {
            this.errorSubject.next(error as Error);
            return [];
        }
    }

    /**
     * Request access to a serial port
     */
    public async requestPort(): Promise<SerialPort | null> {
        if (!this.isSupported()) {
            throw new Error('Web Serial API is not supported in this browser');
        }

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return
            const port = await navigator.serial.requestPort();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return port;
        } catch (error) {
            this.errorSubject.next(error as Error);
            return null;
        }
    }

    /**
     * Connect to a serial port
     */
    public async connect(port?: SerialPort, config: Partial<SerialConnectionConfig> = {}): Promise<boolean> {
        try {
            console.log('[YModem] Connecting to serial port...');
            if (!port && !this.port) {
                const requestedPort = await this.requestPort();
                if (!requestedPort) return false;
                port = requestedPort;
            }

            this.port = port || this.port;
            if (!this.port) return false;

            if (!this.port.readable || !this.port.writable) {
                const defaultConfig: SerialConnectionConfig = {
                    baudRate: 9600,
                    dataBits: 8,
                    stopBits: 1,
                    parity: 'none',
                    flowControl: false
                };

                const connectionConfig = { ...defaultConfig, ...config };

                console.log('[YModem] Opening port with config:', connectionConfig);
                await this.port.open({
                    baudRate: connectionConfig.baudRate,
                    dataBits: connectionConfig.dataBits,
                    stopBits: connectionConfig.stopBits,
                    parity: connectionConfig.parity,
                    flowControl: connectionConfig.flowControl ? 'hardware' : 'none'
                });
                console.log('[YModem] Port opened successfully');
            }
            // Set connection status to true BEFORE setting up reader/writer
            // so that startReading() can check the correct status
            this.connectionSession++;
            this.connectionStatus.next(true);
            this.setupReaderWriter();
            console.log('[YModem] Reader/Writer setup complete');
            return true;
        } catch (error) {
            console.error('[YModem] Connection error:', error);
            this.errorSubject.next(error as Error);
            return false;
        }
    }

    /**
     * Disconnect from the serial port
     */
    public async disconnect(): Promise<void> {
        this.connectionSession++;
        this.isReadLoopActive = false;

        // Safely close reader
        if (this.reader) {
            try {
                // Try to release the lock first
                try {
                    if (this.port?.readable?.locked) {
                        this.reader.releaseLock();
                    }
                } catch {
                    // If release fails, try cancel
                    await this.reader.cancel().catch(() => {
                        // Ignore cancel errors too
                    });
                }
            } catch {
                // Ignore all reader errors
                console.log('[YModem] Reader cleanup completed (with errors ignored)');
            }
            this.reader = null;
        }

        // Safely close writer
        if (this.writer) {
            try {
                await this.writer.close().catch(() => {
                    // Writer may already be closed - ignore
                });
            } catch {
                // Ignore writer close errors
                console.log('[YModem] Writer cleanup completed (with errors ignored)');
            }
            this.writer = null;
        }

        // Safely close port
        if (this.port) {
            try {
                await this.port.close().catch(() => {
                    // Port may already be closed - ignore
                });
            } catch {
                // Ignore port close errors
                console.log('[YModem] Port cleanup completed (with errors ignored)');
            }
            this.port = null;
        }

        this.dataBuffer = [];
        this.dataReceivedListeners = [];
        this.connectionStatus.next(false);
    }

    /**
     * Send data to the serial port
     */
    public async sendData(data: string | Uint8Array): Promise<boolean> {
        if (!this.writer || !this.connectionStatus.value) {
            this.errorSubject.next(new Error('Serial port is not connected'));
            return false;
        }

        try {
            //console.log('Sending data:', data);
            const uint8Array = typeof data === 'string' ? new TextEncoder().encode(data) : data;

            await this.writer.write(uint8Array);
            return true;
        } catch (error) {
            this.errorSubject.next(error as Error);
            return false;
        }
    }

    /**
     * Check if currently connected
     */
    public isConnected(): boolean {
        return this.connectionStatus.value;
    }

    /**
     * Get current port info
     */
    public getPortInfo(): SerialDeviceInfo | null {
        if (!this.port) return null;

        const info = this.port.getInfo();
        return {
            portName: info.usbVendorId ? `USB Device` : 'Serial Port',
            manufacturer: info.usbVendorId?.toString(),
            vendorId: info.usbVendorId?.toString(16),
            productId: info.usbProductId?.toString(16)
        };
    }

    /**
     * Setup reader and writer for the serial port
     */
    private setupReaderWriter(): void {
        if (!this.port) return;

        try {
            if (this.port.readable?.locked) {
                console.log('[YModem] Readable stream is locked, attempting to release reader');
                if (this.reader) {
                    this.reader.releaseLock();
                    this.reader = null;
                }
            }
            if (this.port.writable?.locked) {
                console.log('[YModem] Writable stream is locked, attempting to release writer');
                if (this.writer) {
                    this.writer.releaseLock();
                    this.writer = null;
                }
            }

            if (!this.reader && this.port.readable) {
                this.reader = this.port.readable.getReader();
                console.log('[YModem] Reader created');
                // Note: Persistent read loop NOT auto-started to avoid breaking import functionality
                // Call startBufferedReading() explicitly for Ymodem upload
            }
            if (!this.writer && this.port.writable) {
                this.writer = this.port.writable.getWriter();
                console.log('[YModem] Writer created');
            }
        } catch (error) {
            console.error('[YModem] Error in setupReaderWriter:', error);
            this.reader = null;
            this.writer = null;
        }
    }

    /**
     * Start a persistent background read loop that feeds data into a buffer
     * Used for Ymodem upload. Do NOT use for import (breaks szWaitForSOH/szGetPacket).
     */
    public startBufferedReading(): void {
        if (this.isReadLoopActive || !this.reader) {
            return;
        }

        this.isReadLoopActive = true;
        this.dataBuffer = [];
        console.log('[YModem] Starting persistent read loop');

        const loop = async () => {
            while (this.isReadLoopActive && this.reader) {
                try {
                    const result = await this.reader.read();
                    if (result.done) {
                        console.log('[YModem] Read loop: stream closed');
                        break;
                    }
                    if (result.value) {
                        // Add all bytes to buffer without creating intermediate Array.from
                        for (const byte of result.value) {
                            this.dataBuffer.push(byte);
                        }
                        this.notifyDataReceived();
                    }
                } catch (error) {
                    // Suppress expected disconnection errors (e.g., "Releasing Default reader")
                    // Only log unexpected errors
                    if (!this.isReadLoopActive) {
                        // Normal shutdown - reader was intentionally released
                        console.log('[YModem] Read loop stopped (normal shutdown)');
                    } else {
                        // Unexpected error during active read
                        console.error('[YModem] Read loop error:', error);
                    }
                    break;
                }
            }
            this.isReadLoopActive = false;
            console.log('[YModem] Persistent read loop ended');
        };

        loop().catch((err) => console.error('[YModem] Persistent read loop crashed:', err));
    }

    private notifyDataReceived(): void {
        if (this.dataReceivedListeners.length > 0) {
            const listeners = [...this.dataReceivedListeners];
            for (const listener of listeners) {
                listener();
            }
        }
    }

    /**
     * Drain/clear the buffer of all pending data
     * Useful when device has been sending stale handshake responses (e.g., multiple 'C' characters)
     * that need to be cleared before starting data transfer
     */
    public drainBuffer(): void {
        const clearedBytes = this.dataBuffer.length;
        this.dataBuffer = [];
        console.log(`[YModem] Buffer drained: cleared ${clearedBytes} bytes`);
    }

    /**
     * Stop the buffered reading loop
     * Used when transitioning from command-mode (verification) to binary protocol mode (YModem transfer)
     */
    public stopBufferedReading(): void {
        if (this.isReadLoopActive) {
            console.log('[YModem] Stopping buffered reading for transfer mode');
            this.isReadLoopActive = false;
        }
    }

    /**
     * Clean up resources when the service is destroyed
     */
    public ngOnDestroy(): void {
        void this.disconnect();
    }

    /**
     * Reads data from the port, discarding bytes until a SOH (0x01) byte is
     * encountered. Returns a Uint8Array that starts at the SOH byte and
     * contains the remainder of the received chunk, so the caller can pass it
     * directly as the spill/offset data to szGetPacket.
     * @param {number} [timeout] - milliseconds before rejecting with a timeout error
     */
    public async szWaitForSOH(timeout = 10000): Promise<Uint8Array> {
        return new Promise((resolve, reject) => {
            let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
            let cHandle: ReturnType<typeof setTimeout> | undefined;
            let finished = false;
            const decoder = new TextDecoder();

            const cleanup = () => {
                if (timeoutHandle) clearTimeout(timeoutHandle);
                if (cHandle) clearTimeout(cHandle);
            };

            const handleTimeout = () => {
                if (!finished) {
                    finished = true;
                    cleanup();
                    reject(new Error('timeout'));
                }
            };

            if (timeout) {
                timeoutHandle = setTimeout(handleTimeout, timeout);
            }

            // Debounced C sender: resets every time data arrives so C is only
            // sent after 3 seconds of silence, never mid-burst.
            const resetCSilenceTimer = () => {
                if (cHandle) clearTimeout(cHandle);
                cHandle = setTimeout(() => {
                    if (!finished) {
                        console.log('No SOH for 3 seconds of silence, sending C to prompt device...');
                        void this.sendData(new Uint8Array([SZ_CODES.C]));
                        resetCSilenceTimer();
                    }
                }, 3000);
            };

            resetCSilenceTimer();

            const readLoop = async () => {
                while (this.port?.readable && !finished) {
                    try {
                        const readResult = await this.reader?.read();

                        if (readResult?.done) {
                            if (!finished) {
                                finished = true;
                                cleanup();
                                reject(new Error('stream ended'));
                            }
                            break;
                        }
                        if (readResult?.value) {
                            const chunk = readResult.value;
                            console.log('Received chunk:', decoder.decode(chunk));
                            resetCSilenceTimer();
                            const sohIndex = chunk.indexOf(SZ_CODES.SOH);
                            if (sohIndex !== -1) {
                                if (!finished) {
                                    finished = true;
                                    cleanup();
                                    resolve(chunk.slice(sohIndex));
                                }
                                break;
                            }
                        }
                    } catch {
                        if (!finished) {
                            finished = true;
                            cleanup();
                            reject(new Error('read'));
                        }
                        break;
                    }
                }
                if (!finished) {
                    finished = true;
                    cleanup();
                    reject(new Error('stream ended'));
                }
            };

            void readLoop();
        });
    }

    /**
     * @typedef {Object} GetPacketRetVal
     * @property {number} [size] - the size of the read packet
     * @property {Uint8Array} [spill] - any data read from the port that did not fit inside the packet and that needs to be used as the first bytes of the next packet
     * @property {'EOT' | 'DONE'} [endReason] - whether End Of Transfer was encountered as the first byte of the packet
     *
     * Reads data repeatedly on the provided port. The first byte is inspected for determining the size of the packet.
     * Accepts an existing buffer for reuse, rather than creating a new. This buffer must be at least as large as the largest packet we support receiving, i.e. 1024 bytes plus overhead.
     * The function currently automatically acknowledges each packet with an ACK. Remove that if we need to support e.g. Ymodem-g which does not use packet ACKs.
     * @param {Uint8Array} packetBuffer "outside" buffer to reuse
     * @param {SerialPort} port - the port over which we communicate with the device
     * @param {ReadableStreamDefaultReader} reader
     * @param {WritableStreamDefaultWriter} writer
     * @param {number} [offset] - The number of bytes already present in the buffer (previous spill)
     * @returns {Promise<GetPacketRetVal>}
     */
    public async szGetPacket(packetBuffer: Uint8Array, offset = 0): Promise<GetPacketRetVal> {
        if (!this.port?.readable) {
            throw new Error('Port is not readable');
        }

        // If a previous spill placed an EOT at position 0, handle it immediately.
        if (offset > 0 && packetBuffer[0] === SZ_CODES.EOT) {
            return { endReason: 'EOT' };
        }

        let byteCount = offset;
        let packetSize = offset > 0 ? szPacketSize[packetBuffer[0]] + szPacketSize.overhead : 0;

        // Pre-buffered bytes already form a complete packet — no reads needed.
        // This happens when szWaitForSOH captures more than one packet's worth
        // of data in a single serial chunk.
        if (packetSize > 0 && byteCount >= packetSize) {
            const spill =
                byteCount > packetSize ? new Uint8Array(packetBuffer.slice(packetSize, byteCount)) : undefined;
            if (spill) this.validateSpillHeader(spill);
            await this.writer?.write(this.ackByte);
            return { size: packetSize, spill };
        }

        // Single read loop: handles both the first chunk (header detection) and
        // subsequent chunks (accumulation) without duplicating logic.
        while (this.port?.readable) {
            const { value, done } = await this.szReadValue(this.reader, 10000);

            if (value === 'timeout') {
                throw new Error('No data received for ten seconds');
            }
            if (done) {
                // A clean stream-end before the first byte is a normal DONE signal;
                // mid-packet is unexpected.
                if (byteCount === 0) return { endReason: 'DONE' };
                throw new Error('Stream closed unexpectedly');
            }

            // First bytes received: inspect the header to determine packet size.
            if (byteCount === 0) {
                if (value[0] === SZ_CODES.EOT) return { endReason: 'EOT' };
                if (!(value[0] in szPacketSize)) {
                    const err = new Error(IMPORT_ERRORS.invalidHeaderByte);
                    err.message = `Raw value: [${Array.from(value).join(', ')}]\nDecoded value: ${new TextDecoder().decode(value)}`;
                    throw err;
                }
                packetSize = szPacketSize[value[0]] + szPacketSize.overhead;
            }

            const remaining = packetSize - byteCount;
            if (value.length < remaining) {
                // Common path: chunk fits entirely within remaining packet space, no spill
                packetBuffer.set(value, byteCount);
                byteCount += value.length;
            } else if (value.length === remaining) {
                // Exact fit: packet complete, no spill
                packetBuffer.set(value, byteCount);
                await this.writer?.write(this.ackByte);
                return { size: packetSize };
            } else {
                // Overflow: chunk contains spill bytes
                packetBuffer.set(value.subarray(0, remaining), byteCount);
                const spill = new Uint8Array(value.subarray(remaining));
                this.validateSpillHeader(spill);
                await this.writer?.write(this.ackByte);
                return { size: packetSize, spill };
            }
        }

        throw new Error('Port is not readable');
    }

    private validateSpillHeader(spill: Uint8Array): void {
        if (!(spill[0] in szPacketSize) && spill[0] !== SZ_CODES.EOT) {
            throw new Error('Invalid packet header byte (in spill)');
        }
    }

    /**
     * @param {ReadableStreamDefaultReader<any>} reader
     */
    private async szReadValue(
        reader: ReadableStreamDefaultReader<Uint8Array> | null,
        timeout = 10000
    ): Promise<{ value: Uint8Array | 'timeout'; done: boolean }> {
        let checkTimeout;
        const ret = (await Promise.race([
            reader?.read(),
            new Promise<{ value: Uint8Array | 'timeout'; done?: boolean }>((resolve) => {
                checkTimeout = setTimeout(() => {
                    resolve({ value: 'timeout', done: false });
                }, timeout);
            })
        ])) as { value: Uint8Array | 'timeout'; done: boolean };
        clearTimeout(checkTimeout);
        return ret;
    }

    public async startLoggingReader(): Promise<void> {
        if (!this.reader) {
            if (this.port?.readable) {
                this.reader = this.port.readable.getReader();
            } else {
                console.warn('No readable port available.');
                return;
            }
        }
        const decoder = new TextDecoder();
        try {
            while (this.port?.readable) {
                const { value, done } = await this.reader.read();
                if (done) break;
                if (value) {
                    console.log('Serial data:', decoder.decode(value));
                }
            }
        } catch (err) {
            console.error('Error reading from serial:', err);
        } finally {
            this.reader.releaseLock();
            this.reader = null;
        }
    }

    /**
     * Internal event-driven helper to wait for a byte matching a predicate, skipping ANSI escape sequences.
     * Checks synchronously first for 0ms latency if data is already in buffer.
     * Otherwise subscribes to incoming data notifications instead of polling on an interval.
     */
    private async waitForMatchingByte(
        predicate: (byte: number) => boolean,
        timeoutMs: number,
        description: string
    ): Promise<number> {
        const initialBufferSize = this.dataBuffer.length;
        let byteBuffer: number[] = [];
        let processedIndex = 0;
        const nonAnsiBytes: number[] = [];

        const isAnsiTerminator = (byte: number): boolean => {
            return (byte >= 0x41 && byte <= 0x5a) || (byte >= 0x61 && byte <= 0x7a);
        };

        const tryProcess = (): number | null => {
            while (processedIndex < this.dataBuffer.length) {
                const byte = this.dataBuffer[processedIndex];
                processedIndex++;

                if (byte === 0x1b) {
                    byteBuffer = [byte];
                    continue;
                }

                if (byteBuffer.length > 0) {
                    byteBuffer.push(byte);
                    if (isAnsiTerminator(byte)) {
                        byteBuffer = [];
                    }
                    continue;
                }

                nonAnsiBytes.push(byte);
                if (predicate(byte)) {
                    this.dataBuffer.splice(0, processedIndex);
                    return byte;
                }
            }
            return null;
        };

        // 1. Synchronous check: if byte is already in buffer, resolve immediately (0ms)
        const immediate = tryProcess();
        if (immediate !== null) {
            return immediate;
        }

        // 2. Event-driven check: wait for new data or timeout
        return new Promise<number>((resolve, reject) => {
            let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

            const cleanup = () => {
                if (timeoutHandle) {
                    clearTimeout(timeoutHandle);
                    timeoutHandle = undefined;
                }
                const index = this.dataReceivedListeners.indexOf(onDataReceived);
                if (index !== -1) {
                    this.dataReceivedListeners.splice(index, 1);
                }
            };

            const onDataReceived = () => {
                const result = tryProcess();
                if (result !== null) {
                    cleanup();
                    resolve(result);
                }
            };

            timeoutHandle = setTimeout(() => {
                cleanup();
                const newDataReceived = this.dataBuffer.slice(initialBufferSize);
                const newDataStr = newDataReceived.map((b) => `0x${b.toString(16).toUpperCase()}`).join(', ');
                const examinedStr = nonAnsiBytes.map((b) => `0x${b.toString(16).toUpperCase()}`).join(', ');
                console.error(`[YModem] ⏱ Timeout waiting for ${description} (${timeoutMs}ms)`);
                console.error(`[YModem]   - Processed ${processedIndex} bytes from buffer`);
                console.error(`[YModem]   - Non-ANSI bytes examined: [${examinedStr}]`);
                console.error(`[YModem]   - New data received: [${newDataStr}]`);
                console.error(`[YModem]   - Buffer size after timeout: ${this.dataBuffer.length} bytes (NOT cleared)`);
                reject(new Error(`Timeout waiting for ${description} (${timeoutMs}ms). Received: [${newDataStr}]`));
            }, timeoutMs);

            this.dataReceivedListeners.push(onDataReceived);
        });
    }

    /**
     * Waits for a specific byte from the serial port, skipping ANSI escape sequences
     * Uses buffered data from persistent read loop
     * @param expectedByte - The byte value to wait for (e.g., 0x06 for ACK)
     * @param timeoutMs - Timeout in milliseconds
     * @returns Promise that resolves when the byte is found
     */
    public async waitForByte(expectedByte: number, timeoutMs: number): Promise<void> {
        const expectedName = this.getByteNameForLogging(expectedByte);
        await this.waitForMatchingByte(
            (byte) => byte === expectedByte,
            timeoutMs,
            `${expectedName} (0x${expectedByte.toString(16).toUpperCase()})`
        );
    }

    /**
     * Waits for either ACK or NAK from the serial port, skipping ANSI escape sequences
     * @param timeoutMs - Timeout in milliseconds
     * @returns Promise that resolves with the received byte (ACK or NAK)
     */
    public async waitForAckOrNak(timeoutMs: number): Promise<number> {
        return await this.waitForMatchingByte(
            (byte) => byte === SZ_CODES.ACK || byte === SZ_CODES.NAK,
            timeoutMs,
            'ACK or NAK'
        );
    }

    /**
     * Waits for either ACK, NAK, or C from the serial port, skipping ANSI escape sequences
     * @param timeoutMs - Timeout in milliseconds
     * @returns Promise that resolves with the received byte (ACK, NAK, or C)
     */
    public async waitForAckOrNakOrC(timeoutMs: number): Promise<number> {
        return await this.waitForMatchingByte(
            (byte) => byte === SZ_CODES.ACK || byte === SZ_CODES.NAK || byte === SZ_CODES.C,
            timeoutMs,
            'ACK, NAK, or C'
        );
    }

    private getByteNameForLogging(byte: number): string {
        switch (byte) {
            case SZ_CODES.SOH:
                return 'SOH';
            case SZ_CODES.STX:
                return 'STX';
            case SZ_CODES.EOT:
                return 'EOT';
            case SZ_CODES.ACK:
                return 'ACK';
            case SZ_CODES.NAK:
                return 'NAK';
            case SZ_CODES.C:
                return 'C';
            default:
                return 'UNKNOWN';
        }
    }

    /**
     * Waits for a text response from the serial port until newline (\n) or timeout
     * Filters ANSI escape sequences and returns the accumulated text
     * @param timeoutMs - Timeout in milliseconds
     * @returns Promise<string> - The text response (trimmed)
     * @throws Error if timeout occurs
     */
    public async waitForTextResponse(timeoutMs: number = 5000): Promise<string> {
        const initialBufferSize = this.dataBuffer.length;
        let processedIndex = 0;
        let textBuffer = '';
        let byteBuffer: number[] = [];

        const isAnsiTerminator = (byte: number): boolean => {
            return (byte >= 0x41 && byte <= 0x5a) || (byte >= 0x61 && byte <= 0x7a);
        };

        const tryProcess = (): string | null => {
            while (processedIndex < this.dataBuffer.length) {
                const byte = this.dataBuffer[processedIndex];
                processedIndex++;

                if (byte === 0x1b) {
                    byteBuffer = [byte];
                    continue;
                }

                if (byteBuffer.length > 0) {
                    byteBuffer.push(byte);
                    if (isAnsiTerminator(byte)) {
                        byteBuffer = [];
                    }
                    continue;
                }

                if (byte === 0x0a) {
                    this.dataBuffer.splice(0, processedIndex);
                    return textBuffer.trim();
                }

                if (byte !== 0x0d) {
                    textBuffer += String.fromCharCode(byte);
                }
            }
            return null;
        };

        // Synchronous check first for 0ms latency
        const immediate = tryProcess();
        if (immediate !== null) {
            return immediate;
        }

        return new Promise((resolve, reject) => {
            let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

            const cleanup = () => {
                if (timeoutHandle) {
                    clearTimeout(timeoutHandle);
                    timeoutHandle = undefined;
                }
                const index = this.dataReceivedListeners.indexOf(onDataReceived);
                if (index !== -1) {
                    this.dataReceivedListeners.splice(index, 1);
                }
            };

            const onDataReceived = () => {
                const result = tryProcess();
                if (result !== null) {
                    cleanup();
                    resolve(result);
                }
            };

            timeoutHandle = setTimeout(() => {
                cleanup();
                const newDataReceived = this.dataBuffer.slice(initialBufferSize);
                const newDataStr = newDataReceived.map((b) => `0x${b.toString(16).toUpperCase()}`).join(', ');
                console.error(`[Serial] ⏱ Timeout waiting for text response (${timeoutMs}ms)`);
                console.error(`[Serial]   - Accumulated text: "${textBuffer}"`);
                console.error(`[Serial]   - New data received: [${newDataStr}]`);
                console.error(`[Serial]   - Buffer size after timeout: ${this.dataBuffer.length} bytes`);
                reject(new Error(`Timeout waiting for text response (${timeoutMs}ms). Accumulated: "${textBuffer}"`));
            }, timeoutMs);

            this.dataReceivedListeners.push(onDataReceived);
        });
    }
}
